import React from "react"
import { withFormik } from "formik"
import { string, object, ref } from "yup"

import styles from "./SignUpForms.module.scss"
import PasswordInput from "../../components/PasswordInput/PasswordInput"

const IndividualStep2Form = ({
  values,
  errors,
  handleSubmit,
  handleChange,
  handleBlur,
  touched,
  isSubmitting,
}) => {
  return (
    <form className={styles.form} onSubmit={handleSubmit} id="formikForm">
      <div className={styles.formFields}>
        <PasswordInput
          name="password"
          className={styles.passField}
          label="Password"
          placeholder="Type your password"
          value={values.password}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.password && touched.password}
          errorMessage={
            errors.password && touched.password && errors.password
          }
        />
        <PasswordInput
          name="confirmPassword"
          className={styles.passField}
          label="Confirm password"
          placeholder="Confirm your password"
          value={values.confirmPassword}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.confirmPassword && touched.confirmPassword}
          errorMessage={
            errors.confirmPassword &&
            touched.confirmPassword &&
            errors.confirmPassword
          }
        />
      </div>
      <button disabled={isSubmitting} style={{ display: "none" }} type="submit">
        Sumbit form
      </button>
    </form>
  )
}

const FormikApp = withFormik({
  mapPropsToValues(props) {
    return {
      password: "",
      confirmPassword: "",
    }
  },
  validationSchema: () =>
    object().shape({
      password: string()
        .min(
          8,
          "Enter a combination of at least 8 characters, no more then 32 characters, with a mix of uppercase letters, lower case letters, numbers & symbols",
        )
        .matches(
          /(?=.*[a-z])/,
          "Enter a combination of at least 8 characters, no more then 32 characters, with a mix of uppercase letters, lower case letters, numbers & symbols",
        )
        .matches(
          /(?=.*[A-Z])/,
          "Enter a combination of at least 8 characters, no more then 32 characters, with a mix of uppercase letters, lower case letters, numbers & symbols",
        )
        .matches(
          /(?=.*\d)/,
          "Enter a combination of at least 8 characters, no more then 32 characters, with a mix of uppercase letters, lower case letters, numbers & symbols",
        )
        .matches(
          /(?=.*\W|_)/,
          "Enter a combination of at least 8 characters, no more then 32 characters, with a mix of uppercase letters, lower case letters, numbers & symbols",
        )
        .max(
          32,
          "Please enter a valid password that consists of no more than 32 characters",
        )
        .required("Please enter a password"),
      confirmPassword: string()
        .oneOf(
          [ref("password"), null],
          "Entered passwords didn`t match. Try again.",
        )
        .required("Please confirm your password"),
    }),
  handleSubmit(
    values,
    { setSubmitting, setFieldError, props: { handleSaveUser, questionnaireData } },
  ) {
    setSubmitting(true)
    // Construct fullName from questionnaire data
    const fullName = questionnaireData?.fullName || 
      (questionnaireData?.firstName && questionnaireData?.lastName
        ? `${questionnaireData.firstName} ${questionnaireData.lastName}`
        : "")
    
    // Combine password values with questionnaire data
    const submitValues = {
      ...values,
      fullName,
      email: questionnaireData?.email || "",
    }
    
    handleSaveUser(submitValues)
      .then(() => {
        // Success - navigation happens in handleSaveUser
        setSubmitting(false)
      })
      .catch((error) => {
        setSubmitting(false)
        if (error.response) {
          const status = error.response.status
          const errorData = error.response.data
          
          if (status === 422 || status === 400) {
            // Handle validation errors
            if (errorData?.error) {
              const errorMessage = typeof errorData.error === 'string' 
                ? errorData.error 
                : errorData.error.message || 'Validation error occurred'
              
              // Check if it's a password error
              if (errorMessage.toLowerCase().includes('password')) {
                setFieldError("password", errorMessage)
              } else {
                // Generic error - show on password field as fallback
                setFieldError("password", errorMessage)
              }
            } else if (errorData?.errors && Array.isArray(errorData.errors)) {
              // Handle array of errors
              const passwordError = errorData.errors.find(err => 
                err.toLowerCase().includes('password')
              )
              if (passwordError) {
                setFieldError("password", passwordError)
              } else {
                setFieldError("password", errorData.errors[0] || "Validation error occurred")
              }
            }
          } else if (status === 500) {
            // Handle server errors
            const errorMessage = errorData?.error || 'Server error occurred. Please try again or contact support.'
            setFieldError("password", "Server error occurred. Please try again or contact support.")
            console.error("Server error during signup:", errorMessage)
          }
        } else {
          // Network error or other issues
          setFieldError("password", "Network error. Please check your connection and try again.")
          console.error("Network error during signup:", error)
        }
      })
  },
})(IndividualStep2Form)

export default FormikApp
